/**
 * 实体类型，字段与 docs/API.md 第 1 节一一对应（与 iOS Core/Models.swift 一致）。
 * 接口 JSON 是 snake_case，api.ts 统一转成 camelCase；透传字段（payload、progress 等）里的键保持原样。
 */

/** 任意 JSON（透传字段） */
export type JSONValue = string | number | boolean | null | JSONValue[] | { [k: string]: JSONValue };
export type JSONObject = { [k: string]: JSONValue };

/** 可同步实体的公共字段 */
export interface SyncEntity {
  id: string;
  familyId?: string;
  seq: number;
  createdAt?: number | null;
  updatedAt?: number | null;
  deletedAt?: number | null;
}

export interface Family {
  id: string;
  name: string;
  timezone: string;
  createdAt?: number | null;
}

export interface Parent {
  id: string;
  email: string;
  displayName: string;
  createdAt?: number | null;
}

export interface FamilyMember {
  parentId: string;
  displayName: string;
  email: string;
  /** owner / member */
  role: string;
}

/** children.settings.assistant：未设置的字段用默认值（关闭、qwen-plus、Cherry、契约里的每日次数） */
/** 小助手没起名字时叫它 */
export const DEFAULT_ASSISTANT_NAME = "小蛋糕";

export interface ChildAssistantSettings {
  enabled?: boolean | null;
  /** 小助手的名字（平板上显示、自称用），没有时叫 DEFAULT_ASSISTANT_NAME */
  name?: string | null;
  /** 对话服务：dashscope（默认）/ deepseek / custom */
  provider?: string | null;
  model?: string | null;
  voice?: string | null;
  /** 每天最多提问次数，0 表示不限 */
  dailyLimit?: number | null;
}

export interface ChildSettings {
  /** 免打扰开始 HH:mm */
  quietStart?: string | null;
  quietEnd?: string | null;
  /** 播报语速 0~1 */
  speechRate?: number | null;
  foregroundLockMin?: number | null;
  assistant?: ChildAssistantSettings | null;
  /** 积分目标：在攒分换的奖品（PUT /children/{id}/goal） */
  goalRewardId?: string | null;
}

export interface Child extends SyncEntity {
  name: string;
  grade?: string | null;
  settings?: ChildSettings | null;
  pointsBalance: number;
}

export interface Device {
  id: string;
  familyId?: string | null;
  kind: "student" | "parent" | string;
  childId?: string | null;
  parentId?: string | null;
  /** ios / android / web */
  platform: string;
  name: string;
  model?: string | null;
  appVersion?: string | null;
  lastSeenAt?: number | null;
  revokedAt?: number | null;
  /** 只在 GET /devices 中返回 */
  online?: boolean | null;
}

/** 卸载请求：pending 等平板上确认 / cancelled 平板上取消了 / failed 卸载失败（卸载成功的那一行直接删掉） */
export interface DeviceAppUninstall {
  status: "pending" | "cancelled" | "failed" | string;
  requestedAt: number;
  /** 发起请求的家长名字 */
  requestedBy?: string | null;
  /** 状态变化时间 */
  at: number;
}

/** 安卓平板上已安装的 App（docs/API.md 2.3「平板上的 App」） */
export interface DeviceApp {
  package: string;
  label: string;
  versionName?: string | null;
  versionCode?: number | null;
  /** 系统应用（不能卸载） */
  system: boolean;
  /** 有桌面图标（能从日程打开） */
  launchable: boolean;
  installedAt?: number | null;
  updatedAt?: number | null;
  /** data URL（data:image/webp;base64,...），还没上传时为 null；icons=0 时没有这个字段 */
  icon?: string | null;
  uninstall: DeviceAppUninstall | null;
}

/** GET /devices/{id}/apps */
export interface DeviceAppsResponse {
  device: Device;
  /** 平板最近一次上报时间，从没上报过为 null */
  appsReportedAt: number | null;
  /** 按名称排序 */
  apps: DeviceApp[];
}

/** course 课程 / after_school 课后 / other 其它 */
export type Category = "course" | "after_school" | "other";

/** 时间类型：全天 / 时段 / 截止（几点前做完）/ 到点（docs/API.md 2.4） */
export type TimeMode = "all_day" | "range" | "due" | "point";

/**
 * 日程打开什么，为空只提醒：学习内容（每次发生对应一条学习安排）/ 其它 App（iPad 用链接、安卓用包名打开）
 */
export type EventAction = { type: "content"; contentId: string } | { type: "app"; name: string; app?: string | null; iosUrl?: string | null; androidPackage?: string | null };

export interface ScheduleEvent extends SyncEntity {
  childId: string;
  category: Category | string;
  title: string;
  /** 图标键，见 ui/icons.tsx */
  icon: string;
  color?: string | null;
  location?: string | null;
  notes?: string | null;
  startDate: string;
  startTime: string;
  /** 跨天的日程可以超过 1440（最多 14 天）；全天的是天数 × 1440；截止 / 到点为 0 */
  durationMin: number;
  /** 时间类型（旧数据为空时按 allDay 推断） */
  timeMode?: TimeMode | null;
  /** 全天日程：startTime 固定 00:00 */
  allDay?: boolean | null;
  action?: EventAction | null;
  rrule?: string | null;
  untilDate?: string | null;
  exdates?: string[] | null;
  /** 透传：{ "2026-11-03": { "start_time": "16:30", ... } } */
  overrides?: Record<string, JSONValue> | null;
  /** 任务（孩子点"做完了"、家长确认加分）还是只提醒（放学、上课）；旧数据没有时算任务 */
  task?: boolean | null;
  /** 任务做完、家长确认后加的分（默认 1，学习内容默认 10） */
  pointsReward?: number | null;
  /** 学习内容日程：截止前做完、家长确认时另加的分（默认 5），同步到每次的学习记录 */
  onTimeBonus?: number | null;
}

/** 按时完成奖励的默认值（日程、学习记录没有 onTimeBonus 时） */
export const ON_TIME_BONUS = 5;

export function eventTimeMode(e: Pick<ScheduleEvent, "timeMode" | "allDay">): TimeMode {
  return e.timeMode ?? (e.allDay ? "all_day" : "range");
}

export interface ReminderRule extends SyncEntity {
  childId: string;
  /** event / assignment / habit */
  sourceType: string;
  sourceId?: string | null;
  title?: string | null;
  startDate?: string | null;
  timeLocal?: string | null;
  rrule?: string | null;
  offsetsMin: number[];
  speakText: string;
  sound: string;
  requireAck: boolean;
  repeatIntervalMin?: number | null;
  repeatMax?: number | null;
  escalateAfterMin?: number | null;
  enabled: boolean;
  /** 习惯提醒点"做完了"、家长确认后加的分（默认 1） */
  pointsReward?: number | null;
}

export interface ReminderLog extends SyncEntity {
  childId: string;
  /** scheduled / instant */
  kind: string;
  ruleId?: string | null;
  occurrenceKey: string;
  title: string;
  text: string;
  requireAck?: boolean | null;
  scheduledAt?: number | null;
  firedAt?: number | null;
  ackedAt?: number | null;
  escalatedAt?: number | null;
  deviceId?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
}

export interface Folder extends SyncEntity {
  parentFolderId?: string | null;
  name: string;
  color?: string | null;
  sort?: number | null;
}

export interface Tag extends SyncEntity {
  name: string;
  color?: string | null;
}

export interface Content extends SyncEntity {
  folderId?: string | null;
  tagIds?: string[] | null;
  title: string;
  description?: string | null;
  /** 见 contracts/activities/registry.json */
  activityType: string;
  /** 透传：符合对应模块 Schema，键保持 snake_case */
  payload: JSONValue;
  payloadVersion: number;
  coverAssetId?: string | null;
  estimatedMin?: number | null;
}

export interface Asset extends SyncEntity {
  contentId?: string | null;
  fileName: string;
  mime: string;
  size: number;
  sha256: string;
  /** pending / ready */
  status: string;
}

export type AssignmentStatus = "planned" | "in_progress" | "completed" | "skipped";

export interface Assignment extends SyncEntity {
  childId: string;
  contentId: string;
  scheduleEventId?: string | null;
  plannedDate: string;
  /** 没挂日程时自带的时间：当天 HH:mm + 时长（分钟）；都为空时只有截止时间 */
  startTime?: string | null;
  durationMin?: number | null;
  dueAt?: number | null;
  status: AssignmentStatus | string;
  /** 透传：{ percent, state, duration_sec } */
  progress?: JSONValue | null;
  /** 透传：ActivityResult */
  result?: JSONValue | null;
  startedAt?: number | null;
  completedAt?: number | null;
  pointsReward: number;
  /** 截止前做完另加的分（默认 5） */
  onTimeBonus?: number | null;
  remindIfNotDone?: boolean | null;
}

export interface PointLedgerEntry extends SyncEntity {
  childId: string;
  delta: number;
  reason: string;
  /** assignment / on_time / reminder / event / achievement / series / redemption / manual */
  sourceType: string;
  sourceId?: string | null;
  actorName?: string | null;
  createdAt: number;
}

export interface CardSeries extends SyncEntity {
  name: string;
  description?: string | null;
  completionPoints: number;
  sort?: number | null;
  /** collection 图鉴系列 / story 成长故事（家长现写的故事卡，docs/成就系统.md 第 3 节） */
  kind?: "collection" | "story" | string | null;
}

export type Rarity = "common" | "rare" | "epic" | "legendary";

export interface Card extends SyncEntity {
  seriesId: string;
  no: number;
  name: string;
  /** 卡面上显示的字或表情 */
  glyph: string;
  rarity: Rarity | string;
  imageAssetId?: string | null;
  description?: string | null;
  /** 故事卡属于哪个孩子（图鉴卡为空） */
  childId?: string | null;
  imagePrompt?: string | null;
}

/** 一次发卡，也是一条成长记录 */
export interface ChildCard extends SyncEntity {
  childId: string;
  cardId: string;
  obtainedAt: number;
  /** manual / checkin / achievement */
  sourceType?: string | null;
  sourceId?: string | null;
  /** 为什么发、发生了什么 */
  story?: string | null;
  /** 事情发生的日期 YYYY-MM-DD */
  occurredOn?: string | null;
  points?: number | null;
  actorName?: string | null;
  /** 附的照片（图片附件 id） */
  photoAssetIds?: string[] | null;
}

export interface AchievementRule extends SyncEntity {
  key?: string | null;
  name: string;
  description?: string | null;
  icon?: string | null;
  /** assignment.completed / reminder.acked */
  trigger: string;
  /** 透传：见 docs/成就系统.md */
  condition?: JSONValue | null;
  /** 透传：{ points, card: { series_id?, rarity_min? } } */
  reward?: JSONValue | null;
  /** once / daily / weekly / unlimited */
  period: string;
  enabled: boolean;
}

export interface Reward extends SyncEntity {
  name: string;
  /** park / game / gift / food / movie / toy */
  icon: string;
  costPoints: number;
  stock?: number | null;
  enabled: boolean;
  /** 家长上传的奖品图片，没有时显示图标 */
  imageAssetId?: string | null;
}

export interface Redemption extends SyncEntity {
  childId: string;
  rewardId: string;
  rewardName: string;
  costPoints: number;
  /** pending / approved / rejected / fulfilled */
  status: string;
  requestedAt: number;
  decidedAt?: number | null;
  decidedBy?: string | null;
  note?: string | null;
}

/** 孩子"做完了"、等家长确认的记录（docs/成就系统.md「完成确认」） */
export interface Checkin extends SyncEntity {
  childId: string;
  /** event 日程的某一天 / reminder 某次提醒 / assignment 学习任务 */
  sourceType: "event" | "reminder" | "assignment" | string;
  sourceId: string;
  occurrenceKey: string;
  occurrenceDate?: string | null;
  title: string;
  /** 建议分数；学习任务另有按时奖励 */
  points: number;
  bonusPoints: number;
  /** pending / approved / rejected */
  status: string;
  doneAt: number;
  decidedAt?: number | null;
  awardedPoints?: number | null;
  note?: string | null;
}

/** 日程某一天那次的 occurrence_key（与服务端一致） */
export function eventOccurrenceKey(eventId: string, date: string): string {
  return `event:${eventId}:${date}`;
}

/**
 * 时间表上一项对应的完成确认 occurrence_key：学习内容日程是那天的学习安排 assignment:<id>，
 * 其它日程 event:<id>:<日期>（跨天的用开始那天）
 */
export function checkinKeyOf(item: AgendaItem, date: string): string | null {
  if (item.action?.type === "content") {
    const id = item.assignmentIds[0];
    return id ? `assignment:${id}` : null;
  }
  return eventOccurrenceKey(item.eventId, item.spanStartDate ?? date);
}

// ---------- 接口专用结构 ----------

export interface AgendaItem {
  eventId: string;
  title: string;
  category: Category | string;
  icon: string;
  color?: string | null;
  location?: string | null;
  notes?: string | null;
  startTime: string;
  endTime: string;
  startAt: number;
  endAt: number;
  /** 这天的学习安排（学习内容日程） */
  assignmentIds: string[];
  /** 固定为 event（时间表只有日程一种，docs/API.md 2.4） */
  kind?: string | null;
  timeMode?: TimeMode | null;
  action?: EventAction | null;
  /** 任务还是只提醒（见 ScheduleEvent.task） */
  task?: boolean | null;
  pointsReward?: number | null;
  onTimeBonus?: number | null;
  /** 全天日程（生日、放假等）：不占时间 */
  allDay?: boolean | null;
  /** 跨天的日程每天给一段（startAt 等已截到当天）；span* 是整个日程的起止 */
  spanStartAt?: number | null;
  spanEndAt?: number | null;
  spanStartDate?: string | null;
  spanEndDate?: string | null;
  /** 第几天 / 共几天（从 1 开始） */
  dayIndex?: number | null;
  dayCount?: number | null;
}

export interface AgendaDay {
  date: string;
  items: AgendaItem[];
}

export interface ReminderItem {
  occurrenceKey: string;
  ruleId?: string | null;
  fireAt: number;
  title: string;
  speakText: string;
  subtitle?: string | null;
  category?: string | null;
  icon?: string | null;
  sound?: string | null;
  requireAck: boolean;
  repeats: number[];
  source?: { type: string; id?: string | null } | null;
}

export interface ReminderSchedule {
  generatedAt: number;
  items: ReminderItem[];
}

export interface UploadTicket {
  asset: Asset;
  upload: { method: string; url: string; headers?: Record<string, string> | null };
}

/** /v1/sync 响应里的数据集（键已转成 camelCase） */
export interface SyncData {
  children?: Child[];
  scheduleEvents?: ScheduleEvent[];
  reminderRules?: ReminderRule[];
  reminderLogs?: ReminderLog[];
  folders?: Folder[];
  tags?: Tag[];
  contents?: Content[];
  assets?: Asset[];
  assignments?: Assignment[];
  pointLedger?: PointLedgerEntry[];
  cardSeries?: CardSeries[];
  cards?: Card[];
  childCards?: ChildCard[];
  achievementRules?: AchievementRule[];
  rewards?: Reward[];
  redemptions?: Redemption[];
  checkins?: Checkin[];
}

export interface SyncResponse {
  seq: number;
  full: boolean;
  data: SyncData;
}

// ---------- AgendaItem 辅助函数（与 iOS AgendaItem 一致） ----------

export function agendaItemId(it: AgendaItem): string {
  return `${it.eventId}@${it.startAt}`;
}

export function timeModeOf(it: AgendaItem): TimeMode {
  return it.timeMode ?? (it.allDay ? "all_day" : "range");
}

/** 截止 / 到点：只有一个时间点，不占时段 */
export function isMarker(it: AgendaItem): boolean {
  const m = timeModeOf(it);
  return m === "due" || m === "point";
}

/** 打开学习内容的日程 */
export function isContentItem(it: AgendaItem): boolean {
  return it.action?.type === "content";
}

/** 要孩子做的事（"任务 x/y"算它，有"做完了"）：任务类日程；只提醒的（放学、上课、生日）不算 */
export function isTodo(it: AgendaItem): boolean {
  return isContentItem(it) || it.task !== false;
}

/** 新建日程不填 task 时的默认值（与服务端一致，docs/API.md 2.4）：学习内容、打开其它 App、截止、到点是任务；时段、全天只提醒 */
export function defaultTask(mode: TimeMode, action: EventAction | null | undefined): boolean {
  return action != null || mode === "due" || mode === "point";
}

export function isAllDay(it: AgendaItem): boolean {
  return timeModeOf(it) === "all_day";
}

export function isMultiDay(it: AgendaItem): boolean {
  return (it.dayCount ?? 1) > 1;
}

/** 这一段占满当天：全天日程，或跨天日程中间的整天（00:00–24:00） */
export function coversWholeDay(it: AgendaItem): boolean {
  return isAllDay(it) || (isMultiDay(it) && it.startTime === "00:00" && it.endTime === "24:00");
}

/** 占时间的时段日程："现在 / 正在进行 / 进度条"只看它（不含截止、到点和占满当天的一段） */
export function isTimedEvent(it: AgendaItem): boolean {
  return !isMarker(it) && !coversWholeDay(it);
}

/** "下一项"算它：有具体时间的都算（时段、截止、到点），全天的不算 */
export function hasClockTime(it: AgendaItem): boolean {
  return !coversWholeDay(it);
}

/** 整个日程的起止（跨天的不截到当天） */
export function wholeStartAt(it: AgendaItem): number {
  return it.spanStartAt ?? it.startAt;
}

export function wholeEndAt(it: AgendaItem): number {
  return it.spanEndAt ?? it.endAt;
}

export type DayPart = "single" | "first" | "middle" | "last";

/** 跨天日程的哪一段（占满当天的一段都算 middle） */
export function dayPart(it: AgendaItem): DayPart {
  const i = it.dayIndex;
  const n = it.dayCount;
  if (!isMultiDay(it) || i == null || n == null) return "single";
  if (it.startTime === "00:00" && it.endTime === "24:00") return "middle";
  return i <= 1 ? "first" : i >= n ? "last" : "middle";
}

/** 两行显示的时间（上 / 下）：["18:00", "开始"]、["到", "10:00"]、["全天", null]、["16:00", "17:00"]、截止 ["21:00", "前"]、到点 ["20:30", null] */
export function timeLines(it: AgendaItem): [string, string | null] {
  if (isAllDay(it)) return ["全天", null];
  const m = timeModeOf(it);
  if (m === "due") return [it.startTime, "前"];
  if (m === "point") return [it.startTime, null];
  switch (dayPart(it)) {
    case "single":
      return [it.startTime, it.endTime];
    case "first":
      return [it.startTime, "开始"];
    case "middle":
      return ["全天", null];
    case "last":
      return ["到", it.endTime];
  }
}

/** 一行显示的时间：全天 → "全天"；跨天第一天 → "18:00 开始"；中间天 → "全天"；最后一天 → "到 10:00"；普通 → "16:00–17:00"；截止 → "21:00 前"；到点 → "20:30" */
export function timeText(it: AgendaItem): string {
  const [a, b] = timeLines(it);
  if (b == null) return a;
  if (timeModeOf(it) === "due") return `${a} 前`;
  return dayPart(it) === "single" ? `${a}–${b}` : `${a} ${b}`;
}

/** 只放得下开始时间的地方：普通日程 → "16:00"，其它同 timeText */
export function shortTimeText(it: AgendaItem): string {
  return !isAllDay(it) && dayPart(it) === "single" ? it.startTime : timeText(it);
}

/** 跨天的 "第 2/3 天"，不跨天为 null */
export function dayText(it: AgendaItem): string | null {
  if (!isMultiDay(it) || it.dayIndex == null || it.dayCount == null) return null;
  return `第 ${it.dayIndex}/${it.dayCount} 天`;
}

export function isDone(a: Assignment): boolean {
  return a.status === "completed";
}
